import React, { useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Spin } from 'antd';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import styles from 'LearningManager/styles/CreateAssignment.module.scss';
import {
	ASSIGN_USERS_OPTION,
	SetupOptions,
	AssignmentSetupType,
	quickSetupSteps,
	customSetupSteps,
	getLimitedEditSteps,
	getAssignmentStartDate,
	hasAssignUsersSelection,
	hasConfigureData,
	hasCustomSelection
} from 'LearningManager/constants/createAssignment.constant';
import SetupCard from 'LearningManager/components/SetupCard';
import { Icon, Notification } from 'Components';
import useTenantScope from 'LearningManager/hooks/useTenantScope';
import useAssignmentTenantId from 'LearningManager/hooks/useAssignmentTenantId';
import { useHistory, useLocation } from 'react-router-dom';
import { getAppRoutes } from 'Pages/App/App.constants';
import Stepper from 'LearningManager/components/Stepper';
import {
	StepperProvider,
	useStepper
} from 'LearningManager/hooks/Stepper.context';
import CreateExitModal from 'LearningManager/components/CreateExitModal';
import { clearAssignmentDraft } from 'LearningManager/hooks/useAssignmentDraft';
import { getApiErrorMessages } from 'LearningManager/helpers/apiError';
import Button from 'LearningManager/components/Button';
import AssignmentSuccessModal from 'LearningManager/components/AssignmentSuccessModal';
import classNames from 'classnames';
import moment from 'moment';
import { useStores } from 'Hooks/useStore';
import { LearningPathPayload } from 'Services/Api/LearningManager/Types';
import {
	AssignmentActivity,
	AssignmentExpirationType,
	AssignmentExpirationUnit,
	AssignmentSourcePayload,
	AssignmentSourceLimitedUpdatePayload
} from 'Services/Api/AssignmentSources/Types';
import { restoreScreenScroll } from 'LearningManager/helpers/role';
import { lmsAxiosClient } from 'LearningManager/service/lmsClient';
import ACTIVITIES_API from 'Services/Api/Activities/Api';

// "One year" is the only interval the Configure step offers, so it is fixed.
const CERT_DURATION_INTERVAL_VALUE = 1;
const CERT_DURATION_INTERVAL_UNIT: AssignmentExpirationUnit = 'year';

export const useQuery = () => {
	const location = useLocation();

	return useMemo(
		() => new URLSearchParams(location.search),
		[location.search]
	);
};

const CreateAssignment: React.FC = () => {
	const { t } = useTranslation();

	const query = useQuery();

	const currentSetupType = query.get(
		'setupType'
	) as AssignmentSetupType | null;

	const isLimitedEdit =
		Boolean(query.get('assignmentId')) &&
		query.get('isScheduled') !== 'true';

	const steps = (() => {
		if (!currentSetupType) return null;
		if (isLimitedEdit) return getLimitedEditSteps(currentSetupType);
		if (currentSetupType === 'Quick Setup') return quickSetupSteps;
		if (currentSetupType === 'Custom Setup') return customSetupSteps;
		return null;
	})();

	return (
		<StepperProvider>
			<div id="lms-ca-screen" className={styles.fullScreen}>
				<Header t={t} />
				<div id="lms-ca-main" className={styles.root}>
					<Stepper />
					<div
						id="lms-ca-step-body"
						className={styles.mainContainer}
					>
						<StepRenderer
							currentSetupType={currentSetupType}
							steps={steps}
							t={t}
						/>
					</div>
					<NavigationButtons
						steps={steps}
						t={t}
					/>
				</div>
			</div>
		</StepperProvider>
	);
};

interface StepRendererProps {
	currentSetupType: AssignmentSetupType | null;
	steps:
		| typeof quickSetupSteps
		| typeof customSetupSteps
		| null;
	t: TFunction;
}

const StepRenderer: React.FC<StepRendererProps> = observer(
	({ currentSetupType, steps, t }) => {
		const { current, isAssignmentLoading } = useStepper();
		const CurrentStep = steps?.[current]?.component;

		if (isAssignmentLoading) {
			return (
				<div
					id="lms-ca-step-loading"
					className={styles.loading}
				>
					<Spin size="large" />
				</div>
			);
		}

		if (!currentSetupType || !CurrentStep) {
			return <SelectCourse t={t} />;
		}

		return <CurrentStep />;
	}
);

interface TranslationProp {
	t: TFunction;
}

const Header: React.FC<TranslationProp> = ({ t }) => {
	const history = useHistory();
	const query = useQuery();
	const [showModal, setShowModal] = useState(false);
	const isEditMode = Boolean(query.get('assignmentId'));

	return (
		<div
			id="lms-ca-header"
			className={styles.headerWrapper}
		>
			<div className={styles.header}>
				<div
					id="lms-ca-header-title"
					className={styles.header__heading}
				>
					{isEditMode
						? t('learning.createAssignment.editAssignments')
						: t('learning.createAssignment.createAssignment')}
				</div>

				<div
					id="lms-ca-header-desc"
					className={styles.header__desc}
				>
					{isEditMode
						? t('learning.createAssignment.editDescription')
						: t('learning.createAssignment.createDescription')}
				</div>
			</div>

			<Icon
				id="lms-ca-header-close"
				type="close"
				role="img"
				onClick={() => setShowModal(true)}
			/>

			<CreateExitModal
				visible={showModal}
				onCancel={() => setShowModal(false)}
				onExit={() => {
					if (!isEditMode) clearAssignmentDraft();
					history.push(getAppRoutes().ASSIGNMENTS);
				}}
			/>
		</div>
	);
};

const SelectCourse: React.FC<TranslationProp> = observer(
	({ t }) => {
		const { setupType, setSetupType } = useStepper();
		const {
			canSelectTenant,
			globalTenantId
		} = useTenantScope();

		return (
			<div
				id="lms-ca-select-course"
				className={styles.selectCourse}
			>
				<div className={styles.selectCourse__header}>
					<div className={styles.selectCourse__wrapper}>
						<div
							id="lms-ca-select-course-heading"
							className={styles.selectCourse__heading}
						>
							{t(
								'learning.createAssignment.selectCourses'
							)}
						</div>

						<div
							id="lms-ca-select-course-desc"
							className={styles.selectCourse__desc}
						>
							{t(
								'learning.createAssignment.selectCoursesDescription'
							)}
						</div>

						{canSelectTenant && !globalTenantId && (
							<div
								id="lms-ca-select-course-tenant-hint"
								className={
									styles.selectCourse__tenantHint
								}
							>
								{t(
									'learning.createAssignment.selectTenantHint'
								)}
							</div>
						)}
					</div>
				</div>

				<div
					id="lms-ca-setup-container"
					className={styles.setupContainer}
				>
					{SetupOptions.map((opt) => (
						<SetupCard
							key={opt.title}
							id={`lms-ca-setup-card-${opt.title
								.toLowerCase()
								.replace(/\s+/g, '-')}`}
							{...opt}
							isActive={setupType === opt.title}
							onClick={() =>
								setSetupType(
									opt.title as AssignmentSetupType
								)
							}
						/>
					))}
				</div>
			</div>
		);
	}
);

interface NavigationProps {
	steps:
		| typeof quickSetupSteps
		| typeof customSetupSteps
		| null;
	t: TFunction;
}

const NavigationButtons: React.FC<NavigationProps> = observer(
	({ steps, t }) => {
		const [nextBtnLoading, setNextBtnLoading] = useState(false);
		const history = useHistory();
		const location = useLocation();

		const { learningManagerStore } = useStores();

		const {
			current,
			setupType,
			steps: stepItems,
			draft,
			next,
			back,
			isLimitedEdit
		} = useStepper();

		const { canSelectTenant, globalTenantId } = useTenantScope();

		const assignmentTenantId = useAssignmentTenantId();

		const [showSuccessModal, setShowSuccessModal] =
			useState(false);

		const [isSubmitting, setIsSubmitting] =
			useState(false);

		const query = useQuery();

		const currentSetupType = query.get('setupType');

		// Only a super admin may send tenant_id; everyone else is
		// scoped by their token and the API refuses the field.
		// An assignment being edited keeps the tenant it was
		// created for.
		const tenantId = canSelectTenant
			? assignmentTenantId
			: undefined;

		const hasTenantId =
			!canSelectTenant || tenantId !== undefined;

		const buildPayload = (): LearningPathPayload => {
			const courseIds =
				setupType === 'Quick Setup'
					? draft.quickSetup
							.selectedLearningPathCourseIds
					: [
							...draft.customSetup
								.selectedLearningPathCourseIds,
							...draft.customSetup
								.selectedIndividualCourseIds
						];

			return {
				name: draft.configure.assignmentName,
				description: draft.configure.assignmentName,
				is_required:
					draft.configure.assignmentType ===
					'one-time-assignment',
				order: 1,
				activities: courseIds.map(
					(activity_id, index) => ({
						activity_id,
						is_required: true,
						order: index + 1
					})
				)
			};
		};

		// An activity can belong to multiple learning paths.
		const dedupeActivities = (
			items: AssignmentActivity[]
		): AssignmentActivity[] => {
			const byId = new Map<
				number,
				AssignmentActivity
			>();

			items.forEach((item) => {
				if (!byId.has(item.activity_id)) {
					byId.set(item.activity_id, item);
				}
			});

			return Array.from(byId.values());
		};

		// Quick Setup: every reviewed learning path,
		// scoped org-wide.
		const buildAssignmentSourcePayload =
			(): AssignmentSourcePayload => ({
				...buildConfigureBase(),

				activities: dedupeActivities(
					learningManagerStore.learningPaths.flatMap(
						(lp) =>
							(lp.activities ?? []).map((a) => ({
								activity_id: a.activity_id,
								is_required: a.is_required
							}))
					)
				),

				learning_paths:
					learningManagerStore.learningPaths.map(
						(lp) => ({
							learning_path_id: lp.id,
							is_required: lp.is_required
						})
					),

				scopes: {
					group_labels: ['Organization']
				}
			});

		const buildConfigureBase = () => {
			const { configure } = draft;

			// ISO 8601 with an explicit offset and no milliseconds,
			// as the API wants.
			const toIso = (date: string) =>
				moment
					.utc(date)
					.format('YYYY-MM-DDTHH:mm:ssZ');

			const startDate =
				getAssignmentStartDate(configure);

			const dueDate =
				configure.dueDate === 'custom' &&
				configure.dueDateValue
					? configure.dueDateValue
					: null;

			const expirationType: AssignmentExpirationType =
				configure.certDuration === 'one-year'
					? 'interval'
					: configure.certDuration === 'custom'
						? 'date'
						: 'none';

			// Only the fields the selected expiration type uses
			// are sent.
			const isInterval =
				expirationType === 'interval';

			const isDate =
				expirationType === 'date';

			return {
				name: configure.assignmentName,

				...(tenantId
					? { tenant_id: tenantId }
					: {}),

				start_date: startDate
					? toIso(startDate)
					: null,

				due_date: dueDate
					? toIso(dueDate)
					: null,

				expiration_type: expirationType,

				expiration_interval_value: isInterval
					? CERT_DURATION_INTERVAL_VALUE
					: null,

				expiration_interval_unit: isInterval
					? CERT_DURATION_INTERVAL_UNIT
					: null,

				expiration_date:
					isDate &&
					configure.certExpirationDate
						? moment(
								configure.certExpirationDate
							).format('MM-DD')
						: null,

				is_assigned_to_future_users:
					configure.assignmentType ===
					'ongoing-assignment',

				is_replacing: false
			};
		};

		const buildCustomAssignmentSourcePayload =
			(): AssignmentSourcePayload => {
				const { customSetup } = draft;

				const scopes: AssignmentSourcePayload['scopes'] =
					(() => {
						switch (
							customSetup.selectedAssignUsersOptionId
						) {
							case ASSIGN_USERS_OPTION.ORGANIZATION:
								return {
									group_ids:
										customSetup.selectedOrganizations.map(
											(o) => o.id
										)
								};

							case ASSIGN_USERS_OPTION.PROPERTY:
								return {
									group_ids:
										customSetup.selectedProperties.map(
											(p) => p.id
										)
								};

							case ASSIGN_USERS_OPTION.ROLE:
								return {
									role_ids:
										customSetup.selectedRoles.map(
											(r) => r.id
										)
								};

							case ASSIGN_USERS_OPTION.GROUP:
								return {
									group_ids:
										customSetup.selectedGroups.map(
											(g) => g.id
										)
								};

							case ASSIGN_USERS_OPTION.INDIVIDUAL:
								return {
									user_ids:
										customSetup.selectedIndividualUsers.map(
											(u) => u.id
										)
								};

							default:
								return {};
						}
					})();

				const base = buildConfigureBase();

				return {
					...base,

					is_assigned_to_future_users:
						base.is_assigned_to_future_users &&
						customSetup.selectedAssignUsersOptionId !==
							ASSIGN_USERS_OPTION.INDIVIDUAL,

					activities: dedupeActivities([
						...customSetup.selectedIndividualCourseIds.map(
							(id) => ({
								activity_id: id,
								is_required:
									!customSetup.optionalIndividualCourseIds.includes(
										id
									)
							})
						),

						...learningManagerStore.learningPaths
							.filter((lp) =>
								customSetup.selectedLearningPathCourseIds.includes(
									lp.id
								)
							)
							.flatMap((lp) =>
								(lp.activities ?? []).map((a) => ({
									activity_id: a.activity_id,
									is_required:
										a.is_required &&
										!customSetup.optionalLearningPathIds.includes(
											lp.id
										)
								}))
							)
					]),

					learning_paths:
						customSetup.selectedLearningPathCourseIds.map(
							(id) => ({
								learning_path_id: id,
								is_required:
									!customSetup.optionalLearningPathIds.includes(
										id
									)
							})
						),

					scopes
				};
			};

		// Once the start date has passed, only these fields may
		// be updated.
		const buildLimitedUpdatePayload =
			(): AssignmentSourceLimitedUpdatePayload => {
				const base = buildConfigureBase();

				return {
					name: base.name,
					due_date: base.due_date,
					expiration_type:
						base.expiration_type,
					expiration_interval_value:
						base.expiration_interval_value,
					expiration_interval_unit:
						base.expiration_interval_unit,
					expiration_date:
						base.expiration_date
				};
			};

		const isEditMode =
			Boolean(query.get('assignmentId'));

		const disableBackBtn = current === 0 && isEditMode;

		const checkAssignemtName = async (params: any) => {
			try {
				if(nextBtnLoading) return;
				setNextBtnLoading(true);
				const { data } = await lmsAxiosClient.get(ACTIVITIES_API.CHECK_ASSIGNMENT_NAME, { params });
				const nameExist = data?.name_exists || false;
				if(nameExist) throw new Error(t('learning.createAssignment.nameExistError'));
				return false;
			} catch (error: any) {
				const message = error?.response?.data?.message || error?.response?.message || error?.message || '';
				Notification.error({ description: message ? message : t('learning.createAssignment.genericError')});
				return true;
			} finally {
				setNextBtnLoading(false);
			}
		}

		const onBack = () => {
			if (current > 0) {
				back();
				return;
			}

			if (!disableBackBtn) {
				if (currentSetupType) {
					const params = new URLSearchParams(location.search);
					params.delete('setupType');
					history.push({ pathname: location.pathname, search: params.toString() });
				} else {
					history.push(getAppRoutes().ASSIGNMENTS);
				}
			}
		};

		const onNext = async () => {
			if (!currentSetupType) {
				if (!setupType) return;

				const params = new URLSearchParams(
					location.search
				);

				params.set('setupType', setupType);

				history.push({
					pathname: location.pathname,
					search: params.toString()
				});

				return;
			}

			const assignmentId = query.get('assignmentId');
			if (
				steps &&
				current < steps.length - 1
			) {
				const isConfigureTab = document.querySelector('#lms-ca-configure');
				if(isConfigureTab) {
					const { configure } = draft;
					const params: any = { name: configure.assignmentName };
					if(assignmentId) params['id'] = assignmentId;
					if(canSelectTenant && globalTenantId) params['tenant_id'] = globalTenantId;
					const nameExist = await checkAssignemtName(params);
					if(nameExist) return;
				}
				restoreScreenScroll("#lms-ca-screen", 150);
				next();
				return;
			}

			if (
				currentSetupType &&
				current === stepItems.length - 1
			) {

				// Only create carries tenant_id;
				// the update endpoint takes none.
				const needsTenantId = !assignmentId;

				if (
					needsTenantId &&
					!hasTenantId
				) {
					Notification.error({
						description: canSelectTenant
							? t(
									'learning.createAssignment.selectTenantError'
								)
							: t(
									'learning.createAssignment.tenantResolveError'
								)
					});

					return;
				}

				setIsSubmitting(true);

				try {
					if (assignmentId) {
						if (isLimitedEdit) {
							// Active (start date passed)
							// → limited update only
							await learningManagerStore.updateAssignmentSource(
								Number(assignmentId),
								buildLimitedUpdatePayload()
							);
						} else {
							// Scheduled (start date in future)
							// → full update allowed
							const {
								tenant_id,
								...payload
							} =
								setupType ===
								'Quick Setup'
									? buildAssignmentSourcePayload()
									: buildCustomAssignmentSourcePayload();

							await learningManagerStore.updateAssignmentSource(
								Number(
									assignmentId
								),
								{
									...payload,
									start_date:
										payload.start_date ??
										moment
											.utc()
											.format(
												'YYYY-MM-DDTHH:mm:ssZ'
											)
								}
							);
						}
					} else if (
						setupType === 'Quick Setup'
					) {
						await learningManagerStore.createAssignmentSource(
							buildAssignmentSourcePayload()
						);
					} else {
						// Custom Setup
						await learningManagerStore.createAssignmentSource(
							buildCustomAssignmentSourcePayload()
						);
					}

					// On success only — a failed submit keeps
					// the draft.
					clearAssignmentDraft();
					setShowSuccessModal(true);
				} catch (e) {
					const messages = getApiErrorMessages(e);
					Notification.error({
						description: messages.length
						? messages.map((message) => <div key={message}>{message}</div>)
						: t(
							'learning.createAssignment.genericError'
						)
					});
				} finally {
					setIsSubmitting(false);
				}
			}
		};

		const redirectToCourses = () => {
			history.push(
				getAppRoutes().ASSIGNMENTS
			);
		};

		const isLastStep =
			currentSetupType &&
			current === stepItems.length - 1;

		const nextLabel = isLastStep
			? isEditMode
				? t(
						'learning.createAssignment.confirmUpdate'
					)
				: t(
						'learning.createAssignment.confirmAssign'
					)
			: t('learning.createAssignment.next');

		const hideBackButton =
			isEditMode &&
			currentSetupType === 'Quick Setup' &&
			current === 0;

		// Shared with the draft restore, so both go by
		// the same rules.
		const configureComplete =
			hasConfigureData(
				draft,
				isLimitedEdit
			);

		const customSelectionComplete =
			hasCustomSelection(draft);

		const assignUsersComplete =
			hasAssignUsersSelection(draft);

		const isNextDisabled = (() => {
			// A super admin has to have a tenant selected
			// before leaving Select Courses.
			if (!currentSetupType) {
				return !hasTenantId;
			}

			// Limited edit: only Configure (step 0)
			// needs validation.
			if (isLimitedEdit) {
				if (current === 0) {
					return !configureComplete;
				}

				return false;
			}

			if (setupType === 'Quick Setup') {
				if (current === 1) {
					return !configureComplete;
				}

				return false;
			}

			if (setupType === 'Custom Setup') {
				if (current === 0) {
					return !customSelectionComplete;
				}

				if (current === 1) {
					return !assignUsersComplete;
				}

				if (current === 2) {
					return !configureComplete;
				}

				return false;
			}

			return false;
		})();

		return (
			<div
				id="lms-ca-nav"
				className={classNames(
					styles.navigationButtons,
					{
						[styles[
							'navigationButtons--flex-end'
						]]: hideBackButton
					}
				)}
			>
				{!hideBackButton && (
					<Button
						id="lms-ca-nav-back"
						variant="border"
						size="small"
						onClick={onBack}
						disabled={disableBackBtn}
					>
						<Icon
							type="chevronUp"
							role="img"
							style={{
								rotate: '-90deg'
							}}
						/>

						{t(
							'learning.createAssignment.back'
						)}
					</Button>
				)}

				<Button
					id="lms-ca-nav-next"
					size="small"
					onClick={onNext}
					className={
						isLastStep
							? 'cta-prominent'
							: undefined
					}
					loading={nextBtnLoading}
					disabled={isNextDisabled || nextBtnLoading || isSubmitting}
				>
					{nextLabel}{' '}

					{!isLastStep && (
						<Icon
							type="chevronUp"
							role="img"
							style={{
								rotate: '90deg'
							}}
						/>
					)}
				</Button>

				<AssignmentSuccessModal
					visible={showSuccessModal}
					onClose={redirectToCourses}
					isEditMode={isEditMode}
				/>
			</div>
		);
	}
);

export default CreateAssignment;